/**
 * Tailor CV Modal
 * Reworks the user's EXISTING CV content (reordered skills, reworded descriptions) to
 * surface keywords relevant to a specific job — never invents new experience.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { X, Sparkle, Download } from '@phosphor-icons/react';
import { trackEvent } from '../../../../../utils/analytics/analytics';
import { useAuth } from '../../../../../hooks/useAuth';
import { useAppSelector } from '../../../../../redux/hooks';
import { useEffectiveUserProfilePhoto } from '../../../../../hooks/useEffectiveUserProfilePhoto';
import { EuropassCVContent } from '../../../../../components/EuropassCVContent';
import { deriveSortedCvEntries } from '../../../../../utils/profile/derive-cv-entries';
import { buildTailoredCvData } from '../../../../../utils/profile/build-tailored-cv-data';
import { pickProfileLocaleContent } from '../../../../../utils/profile/pick-profile-locale-content';
import { normalizeSupportedLocale } from '../../../../../utils/routing/locale-from-pathname';
import type { CvData } from '../../../../../utils/profile/generate-cv-pdf';
import type { TailorCvResponse } from '../../../../../types/profile-cv-tailor';
import type { DisplayJob } from '../../../../../types';

export interface TailorCvModalProps {
  job: DisplayJob | null;
  applyUrl: string;
  isOpen: boolean;
  onClose: () => void;
}

function formatDateRange(start: string, end: string): string {
  if (!end) return start;
  return `${start} – ${end}`;
}

type TailorState = 'empty-profile' | 'loading' | 'error' | 'success';

export const TailorCvModal: React.FC<TailorCvModalProps> = ({ job, applyUrl, isOpen, onClose }) => {
  const intl = useIntl();
  const { user } = useAuth();
  const userPhoto = useEffectiveUserProfilePhoto(user);
  // Tailor using whichever language CV matches this job page (falling back to whichever
  // language the user has actually filled in, if that one's empty) — so applying to a job
  // in Finnish uses the Finnish CV, not whatever tab happened to be open on the profile page.
  const pageLocale = normalizeSupportedLocale(intl.locale);
  const profileCvByLocale = useAppSelector((s) => s.jobForm.profileCvByLocale);
  const { profileTitle, profileWorkExperiences: workExperiences, profileEducation: education, profileProjects: projects, profileSkills: skills } =
    pickProfileLocaleContent(profileCvByLocale, pageLocale);

  const [state, setState] = useState<TailorState>('loading');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tailoredData, setTailoredData] = useState<CvData | null>(null);
  const [keywordsHighlighted, setKeywordsHighlighted] = useState<string[]>([]);
  const requestedRef = useRef(false);

  const hasAnyContent = workExperiences.length > 0 || skills.length > 0 || projects.length > 0;

  // Lock body scroll and close on Escape while open.
  useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Reset so reopening (e.g. for a different job) fetches again.
  useEffect(() => {
    if (!isOpen) requestedRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || requestedRef.current) return;

    if (!hasAnyContent) {
      setState('empty-profile');
      return;
    }

    requestedRef.current = true;
    setState('loading');
    setErrorMessage(null);

    const baseCvData: CvData = {
      userName: user?.name ?? null,
      userPhoto,
      profileTitle,
      ...deriveSortedCvEntries({ workExperiences, education, projects }),
      skills,
    };

    const run = async () => {
      try {
        const res = await fetch('/api/profile-cv-tailor', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            job: {
              title: job?.title,
              normalisedTitle: job?.normalisedTitle,
              companyName: job?.companyName,
              description: job?.description,
              requirements: job?.requirements,
              responsibilities: job?.responsibilities,
              skillsAndTechnologies: job?.skillsAndTechnologies,
              specialisations: job?.specialisations,
            },
            profile: {
              profileTitle,
              workExperiences: workExperiences.map((e) => ({ id: e.id, title: e.title, company: e.company, description: e.description })),
              projects: projects.map((p) => ({ id: p.id, title: p.title, description: p.description })),
              skills,
            },
          }),
        });
        const data = (await res.json()) as { tailored?: TailorCvResponse; message?: string; error?: string };

        if (!res.ok || !data.tailored) {
          if (res.status === 503 && data.error === 'service_unavailable') {
            setErrorMessage(
              intl.formatMessage({
                id: 'jobDetailPage.tailorCv.notConfigured',
                defaultMessage: 'CV tailoring is not enabled for this environment.',
                description: 'Shown when OPENAI_API_KEY is missing for CV tailoring',
              })
            );
          } else {
            setErrorMessage(
              data.message ||
                data.error ||
                intl.formatMessage({
                  id: 'jobDetailPage.tailorCv.failed',
                  defaultMessage: 'Could not tailor your CV. Try again later.',
                  description: 'Generic CV tailoring failure',
                })
            );
          }
          setState('error');
          return;
        }

        setTailoredData(buildTailoredCvData(baseCvData, data.tailored));
        setKeywordsHighlighted(data.tailored.keywordsHighlighted);
        setState('success');
      } catch (e) {
        setErrorMessage(
          e instanceof Error
            ? e.message
            : intl.formatMessage({
                id: 'jobDetailPage.tailorCv.networkError',
                defaultMessage: 'Network error. Check your connection.',
                description: 'CV tailoring network failure',
              })
        );
        setState('error');
      }
    };

    void run();
    // Deliberately re-runs only when the modal opens or the profile/job snapshot at that
    // moment changes identity — refetching on every keystroke elsewhere isn't desired.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleDownload = async () => {
    if (!tailoredData) return;
    trackEvent({
      name: 'tailor_cv_interaction',
      params: {
        action: 'download',
        job_id: job?.objectID,
        locale: intl.locale,
      },
    });
    const { generateCvPdf } = await import('../../../../../utils/profile/generate-cv-pdf');
    const doc = generateCvPdf(tailoredData, pageLocale);
    const companySuffix = job?.companyName ? `-${job.companyName}` : '';
    doc.save(`${user?.name || 'cv'}${companySuffix}-tailored-cv.pdf`);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label={intl.formatMessage({
        id: 'jobDetailPage.tailorCv.title',
        defaultMessage: 'Tailor my CV for this job',
        description: 'Tailor CV modal title',
      })}
    >
      <div
        className="relative flex flex-col max-h-[90vh] w-full max-w-2xl bg-white dark:bg-[#1F1509] rounded-2xl shadow-2xl overflow-hidden border border-[#DDD5C8]/60 dark:border-[#4A3520]/50"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#DDD5C8] dark:border-[#4A3520] shrink-0 bg-[#F2EADF]/40 dark:bg-[#2A1D0F]/50">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-r from-[#3B5E45] to-[#5A8A68] text-white">
              <Sparkle className="h-4 w-4" />
            </span>
            <span className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
              <FormattedMessage id="jobDetailPage.tailorCv.title" defaultMessage="Tailor my CV for this job" description="Tailor CV modal title" />
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#EDE3D4] dark:hover:bg-[#342415] text-[#7A6E5F] dark:text-[#B8A48C] cursor-pointer"
            aria-label={intl.formatMessage({ id: 'profile.closeSuggestions', defaultMessage: 'Close', description: 'Close AI suggestions' })}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {state === 'empty-profile' && (
            <div className="text-center py-8 space-y-3">
              <p className="text-sm text-[#3D3529] dark:text-[#B8A48C]">
                <FormattedMessage
                  id="jobDetailPage.tailorCv.emptyProfile"
                  defaultMessage="Fill in your profile first — we need your work experience or skills to tailor a CV."
                  description="Shown when profile has no CV content"
                />
              </p>
              <a
                href="/profile"
                className="inline-block rounded-lg bg-[#3B5E45] hover:bg-[#2A4533] text-white text-sm font-medium px-4 py-2"
              >
                <FormattedMessage id="jobDetailPage.tailorCv.goToProfile" defaultMessage="Go to your profile" description="Link to profile page" />
              </a>
            </div>
          )}

          {state === 'loading' && (
            <div className="text-center py-8 text-sm text-[#7A6E5F] dark:text-[#B8A48C]">
              <FormattedMessage
                id="jobDetailPage.tailorCv.loading"
                defaultMessage="Rewording your existing experience for this job…"
                description="Loading state while tailoring CV"
              />
            </div>
          )}

          {state === 'error' && (
            <div className="px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
              {errorMessage}
            </div>
          )}

          {state === 'success' && tailoredData && (
            <>
              <p className="text-xs text-[#7A6E5F] dark:text-[#B8A48C] leading-relaxed">
                <FormattedMessage
                  id="jobDetailPage.tailorCv.disclaimer"
                  defaultMessage="Only your existing experience was reworded and reordered to highlight what this job cares about — nothing was invented. Review it below before downloading."
                  description="Disclaimer explaining CV tailoring does not fabricate content"
                />
              </p>
              {keywordsHighlighted.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {keywordsHighlighted.map((k) => (
                    <span
                      key={k}
                      className="inline-flex rounded-full bg-[#C8DDCE] dark:bg-[#1A2E22] px-2.5 py-0.5 text-xs text-[#3B5E45] dark:text-[#98CFA8] border border-[#3B5E45]/30 dark:border-[#4A3520]"
                    >
                      {k}
                    </span>
                  ))}
                </div>
              )}
              <div className="rounded-xl overflow-hidden border border-[#DDD5C8] dark:border-[#4A3520]">
                <EuropassCVContent
                  userName={tailoredData.userName}
                  userPhoto={tailoredData.userPhoto}
                  profileTitle={tailoredData.profileTitle}
                  mostRecentWorkExp={tailoredData.mostRecentWorkExp}
                  workExperiencesByDate={tailoredData.workExperiencesByDate}
                  educationByDate={tailoredData.educationByDate}
                  skills={tailoredData.skills}
                  projectsByYear={tailoredData.projectsByYear}
                  formatDateRange={formatDateRange}
                />
              </div>
            </>
          )}
        </div>

        {state === 'success' && tailoredData && (
          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-[#DDD5C8] dark:border-[#4A3520] shrink-0">
            <a
              href={applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-lg bg-[#342415] dark:bg-[#1F1509] text-[#F2EADF] dark:text-[#C4A882] text-sm font-medium hover:bg-[#4A3520] dark:hover:bg-[#342415]"
            >
              <FormattedMessage id="jobDetailPage.applyNow" defaultMessage="Apply on Company Website" description="Apply now button on job detail page" />
            </a>
            <button
              type="button"
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#3B5E45] dark:bg-[#2A4533] text-white text-sm font-medium hover:bg-[#2A4533] dark:hover:bg-[#1A2E22]"
            >
              <Download className="w-4 h-4" />
              <FormattedMessage id="jobDetailPage.tailorCv.download" defaultMessage="Download tailored CV" description="Download tailored CV button" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
